import { narratives, renderCatalogRoute } from './catalog';
import { setupGarden } from './garden-client';
import { setupNarrativeGraph } from './narrative-graph';

document.documentElement.classList.add('js');
const isCatalogRoute = renderCatalogRoute();
setupGarden();

const setupMobileNav = () => {
  document.querySelectorAll<HTMLButtonElement>('.nav-toggle').forEach((toggle) => {
    const nav = document.getElementById(toggle.getAttribute('aria-controls') ?? '');
    if (!nav) return;

    const closeNav = () => {
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    };
    const openNav = () => {
      nav.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    };

    toggle.addEventListener('click', () => {
      if (nav.classList.contains('is-open')) closeNav(); else openNav();
    });
    nav.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeNav));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && nav.classList.contains('is-open')) {
        closeNav();
        toggle.focus();
      }
    });
    document.addEventListener('click', (event) => {
      if (!nav.classList.contains('is-open')) return;
      const target = event.target as Node;
      if (nav.contains(target) || toggle.contains(target)) return;
      closeNav();
    });
    window.matchMedia('(min-width: 761px)').addEventListener('change', (event) => {
      if (event.matches) closeNav();
    });
  });
};

setupMobileNav();

interface FieldPoint { x: number; y: number; r: number; phase: number; }

class ResearchField {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private points: FieldPoint[] = [];
  private width = 0;
  private height = 0;
  private mx = 0;
  private my = 0;
  private tx = 0;
  private ty = 0;
  private reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor() {
    this.canvas = document.querySelector<HTMLCanvasElement>('#research-field')!;
    this.ctx = this.canvas.getContext('2d')!;
    this.resize();
    this.bind();
    this.draw(0);
  }

  private resize = () => {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = Math.min(devicePixelRatio || 1, 2);
    this.width = rect.width;
    this.height = rect.height;
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const count = Math.max(28, Math.floor(this.width / 34));
    this.points = Array.from({ length: count }, (_, i) => ({
      x: ((i * 97) % 1000) / 1000 * this.width,
      y: ((i * 163 + 89) % 1000) / 1000 * this.height,
      r: 1 + (i % 4) * .35,
      phase: i * .71,
    }));
  };

  private bind() {
    window.addEventListener('resize', this.resize, { passive: true });
    window.addEventListener('pointermove', (event) => {
      this.tx = event.clientX / innerWidth * 2 - 1;
      this.ty = event.clientY / innerHeight * 2 - 1;
    }, { passive: true });
    window.addEventListener('pointerleave', () => { this.tx = 0; this.ty = 0; });
  }

  private draw = (time: number) => {
    this.mx += (this.tx - this.mx) * .055;
    this.my += (this.ty - this.my) * .055;
    document.documentElement.style.setProperty('--mx', this.mx.toFixed(4));
    document.documentElement.style.setProperty('--my', this.my.toFixed(4));
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.width, this.height);
    ctx.strokeStyle = 'rgba(126, 176, 161, .10)';
    ctx.lineWidth = 1;
    for (let x = 0; x < this.width; x += 72) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, this.height); ctx.stroke(); }
    for (let y = 0; y < this.height; y += 72) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(this.width, y); ctx.stroke(); }
    this.points.forEach((point, index) => {
      const pulse = this.reduceMotion ? 0 : Math.sin(time * .0007 + point.phase) * 3;
      const x = point.x + pulse + this.mx * (8 + index % 5);
      const y = point.y + Math.cos(time * .0005 + point.phase) * 2 + this.my * (6 + index % 4);
      ctx.beginPath(); ctx.arc(x, y, point.r, 0, Math.PI * 2);
      ctx.fillStyle = index % 7 === 0 ? 'rgba(184,255,74,.72)' : 'rgba(151,206,191,.36)'; ctx.fill();
      if (index % 3 === 0 && index + 1 < this.points.length) {
        const next = this.points[index + 1];
        ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(next.x + this.mx * 5, next.y + this.my * 5);
        ctx.strokeStyle = 'rgba(126,176,161,.12)'; ctx.stroke();
      }
    });
    requestAnimationFrame(this.draw);
  };
}

if (!isCatalogRoute && document.querySelector('#research-field')) new ResearchField();

const revealObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      entry.target.classList.add('is-visible');
      revealObserver.unobserve(entry.target);
    }
  });
}, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' });

document.querySelectorAll<HTMLElement>('.reveal, .reveal-blur').forEach((element) => revealObserver.observe(element));

const pinnedMedia = window.matchMedia('(prefers-reduced-motion: no-preference)');

// Pins `sticky` inside `scroller` and reports scroll progress (0–1) across `getDistance()` pixels.
const setupPinnedScroll = (
  scroller: HTMLElement,
  sticky: HTMLElement,
  activeClass: string,
  getDistance: () => number,
  onProgress: (progress: number, distance: number) => void,
  observed: HTMLElement[] = [],
) => {
  let distance = 0;
  let frame = 0;
  const pinTop = () => parseFloat(getComputedStyle(sticky).top) || 0;

  const update = () => {
    if (!pinnedMedia.matches || distance === 0) return;
    const progress = Math.min(Math.max((pinTop() - scroller.getBoundingClientRect().top) / distance, 0), 1);
    scroller.style.setProperty('--flow-progress', progress.toFixed(4));
    onProgress(progress, distance);
  };

  const measure = () => {
    scroller.classList.toggle(activeClass, pinnedMedia.matches);
    scroller.style.height = '';
    onProgress(0, 0);
    if (!pinnedMedia.matches) return;
    distance = Math.max(0, getDistance());
    scroller.style.height = `${sticky.offsetHeight + distance}px`;
    update();
  };

  window.addEventListener('scroll', () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(update);
  }, { passive: true });
  const observer = new ResizeObserver(measure);
  [sticky, ...observed].forEach((element) => observer.observe(element));
  pinnedMedia.addEventListener('change', measure);
};

const narrativeGraph = document.querySelector<HTMLElement>('[data-narr-graph]');
if (!isCatalogRoute && narrativeGraph) setupNarrativeGraph(narrativeGraph, narratives);

// Ecosystem flowchart: vertical scroll drives horizontal travel.
const flowScroller = document.querySelector<HTMLElement>('[data-flow-scroller]');
const flowSticky = flowScroller?.querySelector<HTMLElement>('.flow-sticky');
const flowTrack = flowScroller?.querySelector<HTMLElement>('.flow');
if (!isCatalogRoute && flowScroller && flowSticky && flowTrack) {
  setupPinnedScroll(flowScroller, flowSticky, 'is-horizontal',
    () => flowTrack.scrollWidth - flowSticky.clientWidth,
    (progress, distance) => { flowTrack.style.transform = distance ? `translate3d(${-progress * distance}px, 0, 0)` : ''; },
    [flowTrack]);
}


// FAQ: animate <details> height on open/close instead of the native jump.
document.querySelectorAll<HTMLDetailsElement>('.faq-item').forEach((item) => {
  const summary = item.querySelector('summary');
  let animation: Animation | null = null;
  summary?.addEventListener('click', (event) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    event.preventDefault();
    const closing = item.open && !item.classList.contains('is-closing');
    const from = item.offsetHeight;
    animation?.cancel();
    item.open = !closing;
    const to = item.offsetHeight;
    item.open = true;
    item.classList.toggle('is-closing', closing);
    item.style.overflow = 'hidden';
    animation = item.animate({ height: [`${from}px`, `${to}px`] }, { duration: closing ? 380 : 480, easing: 'cubic-bezier(.22,1,.36,1)' });
    animation.onfinish = () => {
      animation = null;
      item.style.overflow = '';
      item.classList.remove('is-closing');
      item.open = !closing;
    };
  });
});

const contactForm = document.querySelector<HTMLFormElement>('[data-contact-form]');
const contactStatus = document.querySelector<HTMLElement>('[data-contact-status]');

interface RecaptchaApi {
  execute: (widgetId: number) => void;
  ready?: (callback: () => void) => void;
  render: (container: HTMLElement, options: Record<string, string | (() => void) | ((token: string) => void)>) => number;
  reset: (widgetId: number) => void;
}

declare global {
  interface Window {
    grecaptcha?: RecaptchaApi;
    scientataRecaptchaReady?: () => void;
  }
}

const CONTACT_COOLDOWN_MS = 30_000;
const CONTACT_STORAGE_KEY = 'scientata-contact-last-sent';
let contactSubmissionPending = false;
let recaptchaLoader: Promise<RecaptchaApi> | undefined;
let recaptchaWidgetId: number | undefined;
let captchaResolve: ((token: string) => void) | undefined;
let captchaReject: ((reason?: unknown) => void) | undefined;

const setContactStatus = (kind: 'sending' | 'success' | 'error', message: string) => {
  if (!contactStatus) return;
  contactStatus.hidden = false;
  contactStatus.className = `contact-status is-${kind}`;
  contactStatus.textContent = message;
};

const waitForRecaptcha = (recaptcha: RecaptchaApi) => new Promise<RecaptchaApi>((resolve) => {
  if (recaptcha.ready) recaptcha.ready(() => resolve(recaptcha));
  else resolve(recaptcha);
});

const loadRecaptcha = () => {
  if (window.grecaptcha) return waitForRecaptcha(window.grecaptcha);
  if (recaptchaLoader) return recaptchaLoader;

  recaptchaLoader = new Promise<RecaptchaApi>((resolve, reject) => {
    const script = document.createElement('script');
    window.scientataRecaptchaReady = () => {
      const recaptcha = window.grecaptcha;
      window.scientataRecaptchaReady = undefined;
      if (recaptcha) void waitForRecaptcha(recaptcha).then(resolve);
      else reject(new Error('reCAPTCHA unavailable'));
    };
    script.src = 'https://www.google.com/recaptcha/api.js?onload=scientataRecaptchaReady&render=explicit&hl=pt-BR';
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      window.scientataRecaptchaReady = undefined;
      reject(new Error('reCAPTCHA failed to load'));
    };
    document.head.append(script);
  });

  return recaptchaLoader;
};

const getRecaptchaToken = async (siteKey: string) => {
  const container = contactForm?.querySelector<HTMLElement>('[data-recaptcha-container]');
  if (!container) throw new Error('reCAPTCHA container missing');

  const recaptcha = await loadRecaptcha();
  if (recaptchaWidgetId === undefined) {
    recaptchaWidgetId = recaptcha.render(container, {
      sitekey: siteKey,
      size: 'invisible',
      badge: 'bottomright',
      callback: (token: string) => captchaResolve?.(token),
      'expired-callback': () => captchaReject?.(new Error('reCAPTCHA expired')),
      'error-callback': () => captchaReject?.(new Error('reCAPTCHA failed')),
    });
  } else {
    recaptcha.reset(recaptchaWidgetId);
  }

  return new Promise<string>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      captchaResolve = undefined;
      captchaReject = undefined;
      reject(new Error('reCAPTCHA timeout'));
    }, 20_000);
    captchaResolve = (token) => {
      window.clearTimeout(timeout);
      captchaResolve = undefined;
      captchaReject = undefined;
      resolve(token);
    };
    captchaReject = (reason) => {
      window.clearTimeout(timeout);
      captchaResolve = undefined;
      captchaReject = undefined;
      reject(reason);
    };
    recaptcha.execute(recaptchaWidgetId!);
  });
};

contactForm?.addEventListener('input', () => {
  if (!contactSubmissionPending && contactStatus) contactStatus.hidden = true;
});

contactForm?.addEventListener('submit', async (event) => {
  event.preventDefault();
  if (contactSubmissionPending || !contactForm.reportValidity()) return;

  try {
    const lastSent = Number(window.localStorage.getItem(CONTACT_STORAGE_KEY) || 0);
    const secondsRemaining = Math.ceil((CONTACT_COOLDOWN_MS - (Date.now() - lastSent)) / 1000);
    if (secondsRemaining > 0) {
      setContactStatus('error', `Aguarde ${secondsRemaining}s antes de enviar outra mensagem.`);
      return;
    }
  } catch {
    // The server-side rate limit remains active if local storage is unavailable.
  }

  const submitButton = contactForm.querySelector<HTMLButtonElement>('button[type="submit"]');
  const siteKey = contactForm.dataset.recaptchaSiteKey;
  contactSubmissionPending = true;
  setContactStatus('sending', siteKey ? 'Verificando e enviando com segurança…' : 'Enviando mensagem…');
  if (submitButton) submitButton.disabled = true;

  try {
    const formData = new FormData(contactForm);
    if (siteKey) formData.set('g-recaptcha-response', await getRecaptchaToken(siteKey));

    const formBody = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') formBody.append(key, value);
    });

    const response = await fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: formBody.toString(),
    });
    if (response.status === 429) throw new Error('rate-limit');
    if (!response.ok || response.status === 204) throw new Error('submission-failed');

    contactForm.reset();
    try {
      window.localStorage.setItem(CONTACT_STORAGE_KEY, String(Date.now()));
    } catch {
      // The browser cooldown is an additional convenience layer only.
    }
    setContactStatus('success', 'Mensagem enviada. Obrigado pelo contato — responderemos em breve.');
  } catch (error) {
    const message = error instanceof Error && error.message === 'rate-limit'
      ? 'Muitas tentativas em pouco tempo. Aguarde dois minutos e tente novamente.'
      : 'Não foi possível validar ou enviar agora. Tente novamente ou escreva para email@scientata.com.';
    setContactStatus('error', message);
  } finally {
    contactSubmissionPending = false;
    if (recaptchaWidgetId !== undefined) window.grecaptcha?.reset(recaptchaWidgetId);
    if (submitButton) submitButton.disabled = false;
  }
});

const year = document.querySelector<HTMLElement>('#year');
if (year) year.textContent = String(new Date().getFullYear());
